// src/components/checklists/SignatureCanvas.js
// chklstweb-3a7f1c58 — draw-a-signature box for checklist signature questions.
//
// Pointer events cover mouse, finger and pen. White background, so the JPEG the
// checklist uploads (the Notes & Media photo path only takes .jpg) reads cleanly.
// Not the PortalQuoteDetail pad: that one is local to the portal file and produces
// an SVG path, not an image.

import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

const SignatureCanvas = forwardRef(({ disabled = false, height = 140, onChange }, ref) => {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef(null);
    const [empty, setEmpty] = useState(true);

    const paintBackground = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
    };

    // Size the bitmap for the screen's pixel density once mounted.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = Math.max(1, Math.round(rect.width * ratio));
        canvas.height = Math.max(1, Math.round(rect.height * ratio));
        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#12192D';
        paintBackground();
    }, []);

    const point = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handleDown = (e) => {
        if (disabled) return;
        e.preventDefault();
        canvasRef.current.setPointerCapture?.(e.pointerId);
        drawingRef.current = true;
        lastRef.current = point(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.arc(lastRef.current.x, lastRef.current.y, 1, 0, Math.PI * 2);
        ctx.fillStyle = '#12192D';
        ctx.fill();
        if (empty) { setEmpty(false); onChange?.(false); }
    };

    const handleMove = (e) => {
        if (!drawingRef.current || disabled) return;
        e.preventDefault();
        const p = point(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastRef.current = p;
    };

    const handleUp = () => { drawingRef.current = false; lastRef.current = null; };

    useImperativeHandle(ref, () => ({
        clear: () => { paintBackground(); setEmpty(true); onChange?.(true); },
        isEmpty: () => empty,
        // Resolves to a JPEG File, or null if nothing is drawn.
        toJpegFile: () => new Promise((resolve) => {
            if (empty || !canvasRef.current) { resolve(null); return; }
            canvasRef.current.toBlob(
                (blob) => resolve(blob ? new File([blob], 'signature.jpg', { type: 'image/jpeg' }) : null),
                'image/jpeg',
                0.9,
            );
        }),
    }));

    return (
        <canvas
            ref={canvasRef}
            onPointerDown={handleDown}
            onPointerMove={handleMove}
            onPointerUp={handleUp}
            onPointerLeave={handleUp}
            onPointerCancel={handleUp}
            style={{ height, touchAction: 'none' }}
            className={`w-full rounded-lg border border-dashed ${disabled ? 'border-slate-200 opacity-60' : 'border-slate-300 cursor-crosshair'} bg-white`}
            aria-label="Signature box — draw with your mouse or finger"
        />
    );
});

export default SignatureCanvas;
